/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useAccess.ts
 *     Description     :   Spec 285 — role+license access hooks for the frontend, modeled on
 *                         useCanViewCost. They read the current principal (role + the viewing
 *                         tenant's licenses) from the auth store and compose the SAME shared
 *                         decision the backend enforces (canAccessArea), so what the UI shows
 *                         equals what the API will serve. SuperAdmin is license-exempt.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import { useAuthStore } from '../stores/authStore';
import { canAccessArea, type NavAreaId } from '@shared/access/navAccessPolicy';
import { toRole, isSuperAdmin } from '@shared/access/roles';
import type { License, TenantLicenses } from '@shared/access/licenses';

/** Pure: does a principal hold a given license? SuperAdmin is exempt (always true). */
export function principalHasLicense(
  role: string | null | undefined,
  licenses: TenantLicenses,
  license: License,
): boolean {
  const parsed = toRole(role);
  if (parsed && isSuperAdmin(parsed)) return true;
  return licenses != null && licenses[license] === true;
}

/** True when the current tenant holds the backup license (or the user is SuperAdmin). */
export function useHasBackupLicense(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const licenses = useAuthStore((s) => s.licenses);
  return principalHasLicense(role, licenses, 'backup');
}

/** True when the current tenant holds the ops license (or the user is SuperAdmin). */
export function useHasOpsLicense(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const licenses = useAuthStore((s) => s.licenses);
  return principalHasLicense(role, licenses, 'ops');
}

/** True when the current principal may access the given navigation area (role ∧ license). */
export function useHasAccess(area: NavAreaId): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const licenses = useAuthStore((s) => s.licenses);
  return canAccessArea(area, { role, licenses });
}
